import React, { useState, useEffect, useCallback } from "react";
import { Save, Plus, Trash2, GripVertical } from "lucide-react";
import { supabase } from "../../lib/supabase";

interface LearningOutcomesSectionProps {
  courseId?: string;
  initialData: any[];
}

export default function LearningOutcomesSection({
  courseId,
  initialData,
}: LearningOutcomesSectionProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [outcomes, setOutcomes] = useState<any[]>([]);

  // Initialize outcomes from initialData
  useEffect(() => {
    if (initialData) {
      setOutcomes(
        initialData.map((outcome: any) => ({
          outcome_text: outcome.outcome_text,
          order_number: outcome.order_number,
        })),
      );
    }
  }, [initialData]);

  // Check for changes
  const checkForChanges = useCallback(() => {
    if (!initialData) return false;

    const hasChanges =
      outcomes.length !== initialData.length ||
      outcomes.some((outcome, index) => {
        const initialOutcome = initialData[index];
        return (
          !initialOutcome ||
          outcome.outcome_text !== initialOutcome.outcome_text ||
          outcome.order_number !== initialOutcome.order_number
        );
      });

    setHasUnsavedChanges(hasChanges);
  }, [outcomes, initialData]);

  useEffect(() => {
    checkForChanges();
  }, [outcomes, checkForChanges]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!courseId) return;

      // Delete existing outcomes
      await supabase
        .from("course_learning_outcomes")
        .delete()
        .eq("course_id", courseId);

      // Insert new outcomes
      if (outcomes.length > 0) {
        const outcomesWithCourseId = outcomes.map((outcome) => ({
          ...outcome,
          course_id: courseId,
        }));

        const { error: insertError } = await supabase
          .from("course_learning_outcomes")
          .insert(outcomesWithCourseId);

        if (insertError) throw insertError;
      }

      // Update initialData reference
      Object.assign(initialData, outcomes);
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error saving learning outcomes:", err);
      setError(
        err instanceof Error ? err.message : "Failed to save learning outcomes",
      );
    } finally {
      setSaving(false);
    }
  };

  const addOutcome = () => {
    setOutcomes((prev) => [
      ...prev,
      {
        outcome_text: "",
        order_number: prev.length + 1,
      },
    ]);
  };

  const updateOutcome = (index: number, text: string) => {
    setOutcomes((prev) =>
      prev.map((outcome, i) =>
        i === index ? { ...outcome, outcome_text: text } : outcome,
      ),
    );
  };

  const removeOutcome = (index: number) => {
    setOutcomes((prev) => {
      const newOutcomes = prev.filter((_, i) => i !== index);
      // Update order numbers
      return newOutcomes.map((outcome, i) => ({
        ...outcome,
        order_number: i + 1,
      }));
    });
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-gray-900">
            Learning Outcomes
          </h2>
          <button
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${
              hasUnsavedChanges
                ? "text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                : "text-gray-500 bg-gray-100"
            } disabled:opacity-50 transition-colors duration-200`}
          >
            <Save className="h-5 w-5 mr-2" />
            {saving ? "Saving..." : "Save Outcomes"}
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {outcomes.map((outcome, index) => (
            <div key={index} className="flex items-start gap-4">
              <div className="flex-shrink-0 mt-2">
                <GripVertical className="h-5 w-5 text-gray-400 cursor-move" />
              </div>
              <div className="flex-grow">
                <input
                  type="text"
                  value={outcome.outcome_text}
                  onChange={(e) => updateOutcome(index, e.target.value)}
                  placeholder="Enter learning outcome"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                />
              </div>
              <button
                onClick={() => removeOutcome(index)}
                className="flex-shrink-0 text-red-500 hover:text-red-700"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addOutcome}
          className="mt-4 w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <Plus className="h-5 w-5 inline-block mr-2" />
          Add Learning Outcome
        </button>
      </div>
    </div>
  );
}
